import React, { useEffect, useState } from 'react';
import { meetingService } from '../../services/meetingService';
import { formatFirstSessionDate } from '../../services/workshop2026Service';
import {
  formatCommunityCardSubtitle,
  formatCommunityCardTitle,
  formatInterestListSummary,
  formatInterestedViewerLines,
  WorkshopCommunityViewerRole,
} from '../../utils/workshopRosterText';
import { Meeting } from '../../services/meetingService';
import { useLocale } from '../../context/LocaleContext';
import styles from '../../styles/workshop2026Page.module.css';

export interface WorkshopCommunityHeader {
  title: string;
  subtitle?: string;
}

interface WorkshopCommunitySectionProps {
  meetingCode: string;
  meeting: Meeting | null;
  registrationState: 'interest' | 'confirmed';
  onHeaderChange?: (header: WorkshopCommunityHeader) => void;
}

const WorkshopCommunitySection: React.FC<WorkshopCommunitySectionProps> = ({
  meetingCode,
  meeting,
  registrationState,
  onHeaderChange,
}) => {
  const { t } = useLocale();
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [interestCount, setInterestCount] = useState(0);
  const [workshopStarted, setWorkshopStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const firstSession = formatFirstSessionDate(meeting);
  const viewerRole: WorkshopCommunityViewerRole =
    registrationState === 'confirmed' ? 'confirmed' : 'interest';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const roster = await meetingService.getWorkshopRoster(meetingCode);
        if (cancelled) return;
        setConfirmedCount(roster.confirmed_count ?? 0);
        setInterestCount(roster.interest_count ?? 0);
        setWorkshopStarted(roster.workshop_started);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('workshop.2026.community.load_error'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingCode, t]);

  useEffect(() => {
    if (loading || error) return;
    onHeaderChange?.({
      title: formatCommunityCardTitle(
        viewerRole,
        confirmedCount,
        interestCount,
        workshopStarted
      ),
      subtitle: formatCommunityCardSubtitle(
        viewerRole,
        confirmedCount,
        interestCount,
        workshopStarted
      ),
    });
  }, [
    loading,
    error,
    viewerRole,
    confirmedCount,
    interestCount,
    workshopStarted,
    onHeaderChange,
  ]);
  if (loading) return <p className={styles.helperText}>{t('workshop.2026.community.loading')}</p>;
  if (error) return <p className={styles.helperText}>{error}</p>;

  if (viewerRole === 'interest') {
    const lines = formatInterestedViewerLines(
      confirmedCount,
      interestCount,
      workshopStarted,
      true
    );
    return (
      <div className={styles.communityBody}>
        {lines.map((line) => (
          <p key={line} className={styles.communityText}>
            {line}
          </p>
        ))}
      </div>
    );
  }

  const showInterestSection = !workshopStarted && interestCount > 0;

  // Confirmed members: phone list lives in this card; no name roster once workshop is underway.
  // Pre-start still shows interest-list summary + phone-list timing note.
  if (workshopStarted && !showInterestSection) {
    return null;
  }

  return (
    <div className={styles.communityBody}>
      {showInterestSection && (
        <div className={styles.rosterBlock}>
          <p className={styles.rosterLabel}>
            {t('workshop.2026.community.still_deciding', { count: interestCount })}
          </p>
          <p className={styles.communityText}>{formatInterestListSummary(interestCount)}</p>
        </div>
      )}

      {!workshopStarted && (
        <p className={styles.helperText}>
          {firstSession !== 'date TBD'
            ? t('workshop.2026.community.lists_at_start', { firstSession })
            : t('workshop.2026.community.lists_at_start_tbd')}
        </p>
      )}
    </div>
  );
};

export default WorkshopCommunitySection;
